import { debounce } from "lodash";
import { IQuery, IQueryResponse } from "../server/query";
import { ServerUtility } from "../server/serverUtility";
import { IPromise } from "angular";

type GetRowsParams = {
  startRow: number;
  endRow: number;
  successCallback: (rowsThisPage: any[], lastRow: number) => void;
  failCallback: () => void;
};

export class RecordDataSource implements awk.grid.IDataSource {
  pageSize: number = 10;
  rowCount: number = -1;
  overflowSize: number = -1; //only used in virtual paging which we don't use
  maxConcurrentRequests: number = 1; //only used in virtual paging which we don't use
  maxPagesInCache: number = 10; //only used in virtual paging which we don't use

  getOrderByField: () => string | undefined = () => undefined;

  query: IQuery = {};
  private readonly debouncedGetRows: (args: GetRowsParams) => void;
  useWrappedQuery: boolean;

  constructor(
    private readonly serverUtility: ServerUtility,
    readonly url: string,
    useWrappedQuery?: boolean
  ) {
    this.useWrappedQuery = useWrappedQuery ?? false;
    this.debouncedGetRows = debounce((args) => this.getRowsInternal(args), 25);
  }

  getRows(params: GetRowsParams) {
    this.debouncedGetRows(params);
  }

  private getRowsInternal(params: GetRowsParams): void {
    const query = { ...this.query };
    query.Take = params.endRow - params.startRow;
    query.Skip = params.startRow;
    query.OrderBy = this.getSanitizedOrderByField();

    const queryPromise = this.useWrappedQuery
      ? this.serverUtility.queryWrapped({
          url: this.url,
          query,
        })
      : this.serverUtility.query({ url: this.url, query });

    queryPromise
      .then((result) => {
        params.successCallback(result.Results, result.Total);
      })
      .catch((_) => {
        params.failCallback();
      });
  }

  protected getServerPromise(
    url: string,
    query: any
  ): IPromise<IQueryResponse<any>> {
    return this.serverUtility.query<any>({ url, query });
  }

  private getSanitizedOrderByField(): string | undefined {
    const rawOrderBy = this.getOrderByField();
    if (rawOrderBy === undefined) {
      return undefined;
    }
    const orderByFields = rawOrderBy.split(",");
    const alreadySeen = new Set<string>();
    let finalOrderBy = "";
    orderByFields.forEach((f) => {
      const fieldName = f.startsWith("-") ? f.substring(1) : f;
      if (!alreadySeen.has(fieldName)) {
        alreadySeen.add(fieldName);
        finalOrderBy = `${finalOrderBy},${f}`;
      }
    });
    return finalOrderBy.length === 0 ? undefined : finalOrderBy.substring(1);
  }
}
